import { useFormik } from 'formik';
import React, { useRef } from 'react';
import * as yup from 'yup';
import { Input, Modal } from '../ui';

interface UpdateNameDialogProps {
  isOpen: boolean;
  isUpdatingName?: boolean;
  firstName: string;
  lastName: string;
  onClose: () => void;
  onSave: (firstName: string, lastName: string) => void;
}

export default function UpdateNameDialog(props: UpdateNameDialogProps) {
  const cancelButtonRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      firstName: props.firstName,
      lastName: props.lastName,
    },
    validationSchema: yup.object().shape({
      firstName: yup.string().required('First Name is required'),
      lastName: yup.string().required('Last Name is required'),
    }),
    onSubmit: values => {
      props.onSave(values.firstName, values.lastName);
      props.onClose();
    },
  });

  const handleOnCancel = () => {
    formik.resetForm();
    props.onClose();
  };

  return (
    <Modal isOpen={props.isOpen} onClose={handleOnCancel} initialFocus={cancelButtonRef}>
      <div className="sm:flex sm:items-start">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Update your name</h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>Change the name you want associated with your account.</p>
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className="grid grid-cols-1 gap-y-6 gap-x-4 grid-cols-6 mt-3"
        >
          <div className="col-span-3">
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              First name
            </label>
            <div className="mt-1">
              <Input
                type="text"
                name="firstName"
                id="firstName"
                required
                autoComplete="given-name"
                error={
                  formik.touched.firstName && formik.errors.firstName
                    ? formik.errors.firstName
                    : undefined
                }
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
          </div>

          <div className="col-span-3">
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Last name
            </label>
            <div className="mt-1">
              <Input
                type="text"
                name="lastName"
                id="lastName"
                required
                autoComplete="family-name"
                error={
                  formik.touched.lastName && formik.errors.lastName
                    ? formik.errors.lastName
                    : undefined
                }
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
          </div>

          <div className="col-span-6">
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="mt-3 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-xs font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                disabled={!formik.isValid}
              >
                Save
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:mt-0 sm:w-auto sm:text-sm"
                onClick={handleOnCancel}
                ref={cancelButtonRef}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
